// src/pages/kpis/KpiMttrPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";

import PeriodFilter from "../../components/Kpis/PeriodFilter";

import { db } from "../../config/firebase";
import { collection, getDocs, limit, query, Timestamp, where } from "firebase/firestore";

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  return null;
}


function hoursBetween(a, b) {
  if (!a || !b) return null;
  const da = a instanceof Date ? a : a.toDate ? a.toDate() : new Date(a);
  const dbb = b instanceof Date ? b : b.toDate ? b.toDate() : new Date(b);
  const ms = dbb.getTime() - da.getTime();
  if (Number.isNaN(ms)) return null;
  return ms / 36e5;
}

function fmt(n, casas = 1) {
  if (n == null || Number.isNaN(n)) return "-";
  return Number(n).toFixed(casas);
}

export default function KpiMttrPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [periodo, setPeriodo] = useState({ preset: "30D", start: null, end: null });

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [resumo, setResumo] = useState({
    atendimentos: 0,
    mttrHoras: null,
    totalHoras: null,
  });
  const [topMaquinas, setTopMaquinas] = useState([]);

  const canLoad = useMemo(() => {
    return !!usuario?.grupoId;
  }, [usuario]);

  async function carregar() {
    if (!canLoad) return;
    if (!periodo?.start || !periodo?.end) return;

    setCarregando(true);
    setErro("");

    try {
      const ref = collection(db, "atendimentos");
      const q = query(ref, where("grupoId", "==", usuario.grupoId), limit(5000));

      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.dataFim);

      // Máquinas-chave: MTTR apenas para máquinas-chave (coerente com MTBF Global)
      const maqRef = collection(db, "maquinas");
      const qMaq = query(maqRef, where("grupoId", "==", usuario.grupoId), where("maquinaChave", "==", true), limit(5000));
      const maqSnap = await getDocs(qMaq);
      const maquinasChave = maqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const chaveIds = new Set(maquinasChave.map((m) => String(m.id)));
      const chaveCodigos = new Set(maquinasChave.map((m) => String(m.codigo || "").trim()).filter(Boolean));
      const chaveNomes = new Set(maquinasChave.map((m) => String(m.nome || "").trim().toLowerCase()).filter(Boolean));

      const isAtendimentoDeMaquinaChave = (a) => {
        const id = a?.maquinaId || a?.maquina?.id || a?.maquinaRefId || null;
        const cod = a?.maquinaCodigo || a?.maquinaCod || a?.codigoMaquina || null;
        const nome = a?.maquinaNome || a?.maquinaNomeSnapshot || null;

        if (id && chaveIds.has(String(id))) return true;
        if (cod && chaveCodigos.has(String(cod).trim())) return true;
        if (nome && chaveNomes.has(String(nome).trim().toLowerCase())) return true;
        return false;
      };

      const startMs = periodo.start.getTime();
      const endMs = periodo.end.getTime();
      const lista = all
        .filter((a) => {
          const di = tsToDate(a.dataInicio);
          if (!di) return false;
          const ms = di.getTime();
          return ms >= startMs && ms <= endMs && isAtendimentoDeMaquinaChave(a);
        })
        .sort((a, b) => (tsToDate(b.dataInicio)?.getTime() || 0) - (tsToDate(a.dataInicio)?.getTime() || 0));

      const duracoes = lista
        .map((a) => hoursBetween(a.dataInicio, a.dataFim))
        .filter((h) => typeof h === "number" && h >= 0);

      const totalHoras = duracoes.reduce((acc, h) => acc + h, 0);
      const mttrHoras = duracoes.length ? totalHoras / duracoes.length : null;

      // Top máquinas por quantidade de atendimentos no período
      const map = new Map();
      for (const a of lista) {
        const key = a.maquinaNome || a.maquinaId || "(Sem máquina)";
        map.set(key, (map.get(key) || 0) + 1);
      }
      const top = Array.from(map.entries())
        .map(([maquina, total]) => ({ maquina, total }))
        .sort((x, y) => y.total - x.total)
        .slice(0, 10);

      setResumo({ atendimentos: lista.length, mttrHoras, totalHoras });
      setTopMaquinas(top);
    } catch (e) {
      console.error(e);
      setErro("Erro ao carregar MTTR. Verifique índices/consultas do Firestore.");
      setResumo({ atendimentos: 0, mttrHoras: null, totalHoras: null });
      setTopMaquinas([]);
    } finally {
      setCarregando(false);
    }
  }

  // Carrega sempre que aplica período
  useEffect(() => {
    if (periodo?.start && periodo?.end) {
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId, periodo?.start, periodo?.end]);

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div className="pc-card" style={{ width: "100%" }}>
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>MTTR</h3>
            <p>Tempo médio de reparo (base: atendimentos finalizados no período).</p>
          </div>
        </header>

        <PeriodFilter
          value={periodo}
          onApply={(v) => setPeriodo(v)}
        />

        {erro && (
          <div className="pc-alert pc-alert-error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Atendimentos finalizados (máquinas-chave)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumo.atendimentos}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>MTTR (horas)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(resumo.mttrHoras, 2)}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Horas totais (somatório)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(resumo.totalHoras, 1)}</div>
          </div>
        </div>

        <div className="pc-card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Top máquinas (por atendimentos)</div>
          {carregando ? (
            <div>Carregando...</div>
          ) : topMaquinas.length ? (
            <div style={{ overflowX: "auto" }}>
              <table className="pc-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Máquina</th>
                    <th style={{ textAlign: "right" }}>Atendimentos</th>
                  </tr>
                </thead>
                <tbody>
                  {topMaquinas.map((r) => (
                    <tr key={r.maquina}>
                      <td>{r.maquina}</td>
                      <td style={{ textAlign: "right" }}>{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ opacity: 0.8 }}>Sem dados no período.</div>
          )}
        </div>
      </div>
    </section>
  );

  return isMobile ? (
    <LayoutMobile
      tema={tema}
      alterarTema={alterarTema}
      titulo="KPIs — MTTR"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
    >
      {conteudo}
    </LayoutMobile>
  ) : (
    <LayoutDesktop tema={tema} titulo="KPIs" subtitulo="MTTR">
      {conteudo}
    </LayoutDesktop>
  );
}